import React from "react";
import styled from "styled-components";
import { FormattedMessage, useIntl } from "react-intl";
import { msgs } from "../msgs";
import { QuickAction } from "../types";
import { ReactComponent as SparkleGradientIcon } from "../assets/sparkle-gradient.svg";

const ICON_COLOR = "#4D54D1";
const CARD_BG = "#F5F5F5";

const ActionCard = styled.button`
  background: ${CARD_BG};
`;

const ActionIcon = styled.span`
  color: ${ICON_COLOR};
`;

const ActionTitle = styled.span`
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 1;
`;

const ActionDesc = styled.span`
  font-family: "Poppins", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.35;
`;

export const QUICK_ACTIONS: QuickAction[] = [
  {
    key: "create",
    icon: "add_box",
    title: msgs.actionCreateTitle,
    desc: msgs.actionCreateDesc,
    prompt: msgs.promptCreate,
  },
  {
    key: "edit",
    icon: "edit_square",
    title: msgs.actionEditTitle,
    desc: msgs.actionEditDesc,
    prompt: msgs.promptEdit,
  },
  {
    key: "view",
    icon: "visibility",
    title: msgs.actionViewTitle,
    desc: msgs.actionViewDesc,
    prompt: msgs.promptView,
  },
  {
    key: "toggle",
    icon: "change_circle",
    title: msgs.actionToggleTitle,
    desc: msgs.actionToggleDesc,
    prompt: msgs.promptToggle,
  },
  {
    key: "capabilities",
    icon: "help",
    title: msgs.actionCapabilitiesTitle,
    desc: msgs.actionCapabilitiesDesc,
    prompt: msgs.promptCapabilities,
  },
];

export interface WelcomeScreenProps {
  onAction: (a: QuickAction) => void;
}

export function WelcomeScreen({ onAction }: WelcomeScreenProps): React.JSX.Element {
  const intl = useIntl();
  return (
    <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="flex flex-col items-center text-center">
        <SparkleGradientIcon className="w-9 h-9" aria-hidden />
        <h3 className="mt-2 text-base font-semibold text-[#1F2937]">
          <FormattedMessage {...msgs.welcomeTitle} />
        </h3>
        <p className="mt-1 text-sm text-[#6B7280] max-w-[280px]">
          <FormattedMessage {...msgs.welcomeSubtitle} />
        </p>
      </div>

      <div className="mt-4 mb-2 text-[11px] tracking-wider text-center text-[#9CA3AF] font-semibold">
        <FormattedMessage {...msgs.quickActionsLabel} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        {QUICK_ACTIONS.map(action => (
          <ActionCard
            key={action.key}
            type="button"
            onClick={() => onAction(action)}
            className="flex flex-col min-h-[92px] text-left rounded-xl p-3.5 transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-cyan-300">
            <span className="flex items-center gap-2">
              <ActionIcon className="material-symbols-outlined text-xl">{action.icon}</ActionIcon>
              <ActionTitle className="text-[#212121]">
                {intl.formatMessage(action.title)}
              </ActionTitle>
            </span>
            <ActionDesc className="block mt-2 text-[#6B7280]">
              {intl.formatMessage(action.desc)}
            </ActionDesc>
          </ActionCard>
        ))}
      </div>
    </div>
  );
}
